import React, { useCallback, useEffect, useRef, useState } from "react";
import { ChevronLeft, ChevronRight, Loader2, X, GripVertical } from "lucide-react";
import { api } from "@/lib/api";
import { toast } from "sonner";

const HOURS = Array.from({ length: 24 }, (_, h) => h);
const MOVABLE = new Set(["DRAFT", "PENDING_APPROVAL", "APPROVED", "SCHEDULED", "REJECTED"]);
const CARD = {
  DRAFT: "bg-stone-100 border-stone-300 text-stone-700",
  PENDING_APPROVAL: "bg-yellow-50 border-yellow-400 text-yellow-900",
  APPROVED: "bg-emerald-50 border-emerald-400 text-emerald-900",
  SCHEDULED: "bg-indigo-50 border-indigo-400 text-indigo-900",
  SIMULATED_PUBLISHED: "bg-amber-50 border-amber-400 text-amber-900 border-dashed",
  FAILED: "bg-rose-50 border-rose-500 text-rose-900",
  REJECTED: "bg-rose-50 border-rose-300 text-rose-700",
  CANCELLED: "bg-stone-50 border-stone-200 text-stone-400 line-through",
};
const STATUS_TEXT = { SIMULATED_PUBLISHED: "Simulated · not posted", PENDING_APPROVAL: "Pending approval" };
const NEXT = { DRAFT: ["submit"], REJECTED: ["submit"], PENDING_APPROVAL: ["approve", "reject"], APPROVED: ["schedule", "cancel"], SCHEDULED: ["cancel"] };
const errMsg = (e) => {
  const d = e?.response?.data?.detail;
  return typeof d === "string" ? d : d?.message || "Request failed";
};

function Card({ it, onOpen, compact }) {
  const movable = MOVABLE.has(it.status);
  return (
    <button
      type="button"
      data-testid={`week-card-${it.item_id}`}
      draggable={movable}
      onDragStart={(e) => { e.dataTransfer.setData("text/plain", JSON.stringify({ id: it.item_id, v: it.version, t: it.local_time })); }}
      onClick={() => onOpen(it)}
      className={`w-full text-left rounded-lg border px-1.5 py-1 text-[10px] leading-tight transition-transform hover:-translate-y-px ${CARD[it.status] || CARD.DRAFT} ${movable ? "cursor-grab" : "cursor-default"}`}
    >
      <div className="flex items-center gap-1 font-bold">
        {movable && !compact && <GripVertical className="w-3 h-3 opacity-50" />}
        <span>{it.local_time}</span><span className="font-mono opacity-70">{it.platform}</span>
      </div>
      <div className="truncate font-semibold">{it.campaign_name || it.title}</div>
      <div className="uppercase tracking-wide opacity-80" data-testid={`week-card-status-${it.item_id}`}>{STATUS_TEXT[it.status] || it.status}</div>
    </button>
  );
}

function DetailPanel({ it, tz, onClose, onChanged }) {
  const [local, setLocal] = useState(`${it.local_date}T${it.local_time}`);
  const act = async (a) => {
    try { await api.post(`/aina/automation/calendar/${it.item_id}/${a}`, {}); toast.success(`${a} ok`); onChanged(); onClose(); } catch (e) { toast.error(errMsg(e)); }
  };
  const move = async () => {
    try {
      await api.post(`/aina/automation/calendar/${it.item_id}/reschedule`, { local_datetime: local, expected_version: it.version });
      toast.success("Rescheduled"); onChanged(); onClose();
    } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <div className="fixed inset-0 z-50 flex justify-end bg-black/30" data-testid="week-detail-panel" onClick={onClose}>
      <div className="w-full max-w-md h-full bg-white p-5 overflow-y-auto shadow-2xl" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-start justify-between gap-2">
          <h3 className="font-bold text-base" data-testid="week-detail-title">{it.title}</h3>
          <button data-testid="week-detail-close" onClick={onClose}><X className="w-5 h-5" /></button>
        </div>
        <dl className="mt-3 grid grid-cols-3 gap-y-1 text-xs">
          <dt className="text-stone-500">Status</dt><dd className="col-span-2 font-semibold" data-testid="week-detail-status">{STATUS_TEXT[it.status] || it.status}</dd>
          <dt className="text-stone-500">When</dt><dd className="col-span-2">{it.local_date} {it.local_time} ({tz})</dd>
          <dt className="text-stone-500">Platform</dt><dd className="col-span-2">{it.platform}</dd>
          <dt className="text-stone-500">Campaign</dt><dd className="col-span-2">{it.campaign_name || it.campaign_ref || "—"}</dd>
          <dt className="text-stone-500">Draft</dt><dd className="col-span-2">{it.draft_ref ? `${it.draft_ref.draft_id} · v${it.draft_ref.version}` : "—"}</dd>
          <dt className="text-stone-500">Approval</dt><dd className="col-span-2">{it.approval ? `${it.approval.source || "approved"} · ${it.approval.by}` : "Not approved"}</dd>
          <dt className="text-stone-500">Publish</dt><dd className="col-span-2">{it.publish?.state || "—"}{it.status === "SIMULATED_PUBLISHED" && " (simulated, nothing posted)"}</dd>
          <dt className="text-stone-500">Version</dt><dd className="col-span-2">{it.version}</dd>
        </dl>
        <p className="mt-3 text-xs whitespace-pre-wrap bg-stone-50 rounded-lg p-2" data-testid="week-detail-caption">{it.caption}</p>
        {MOVABLE.has(it.status) && (
          <div className="mt-4 flex gap-2 items-center">
            <input data-testid="week-detail-datetime" type="datetime-local" value={local} onChange={(e) => setLocal(e.target.value)} className="border border-stone-200 rounded-lg px-2 py-1 text-xs" />
            <button data-testid="week-detail-reschedule" onClick={move} className="px-3 py-1.5 rounded-full bg-stone-900 text-white text-xs font-semibold">Move</button>
          </div>
        )}
        <div className="mt-4 flex flex-wrap gap-2">
          {(NEXT[it.status] || []).map((a) => (
            <button key={a} data-testid={`week-detail-${a}`} onClick={() => act(a)} className="px-3 py-1.5 rounded-full bg-stone-100 text-stone-800 text-xs font-semibold capitalize">{a}</button>
          ))}
        </div>
      </div>
    </div>
  );
}

function Grid({ data, onOpen, onDrop }) {
  const at = (day, h) => data.items.filter((i) => i.local_date === day && Number(i.local_time.slice(0, 2)) === h);
  const ref = useRef(null);
  const firstHour = data.items.length ? Math.min(...data.items.map((i) => Number(i.local_time.slice(0, 2)))) : 8;
  useEffect(() => { if (ref.current) ref.current.scrollTop = Math.max(0, firstHour - 1) * 45; }, [firstHour, data.week_start]);
  return (
    <div ref={ref} className="hidden md:block max-h-[560px] overflow-y-auto border border-stone-200 rounded-xl" data-testid="week-grid">
      <div className="grid sticky top-0 z-10 bg-white border-b border-stone-200" style={{ gridTemplateColumns: "56px repeat(7, minmax(0,1fr))" }}>
        <div />
        {data.days.map((d) => (
          <div key={d} className="text-[11px] font-bold text-center py-2" data-testid={`week-day-${d}`}>
            {new Date(`${d}T12:00:00`).toLocaleDateString(undefined, { weekday: "short", day: "numeric", month: "short" })}
          </div>
        ))}
      </div>
      {HOURS.map((h) => (
        <div key={h} className="grid border-b border-stone-100" style={{ gridTemplateColumns: "56px repeat(7, minmax(0,1fr))" }}>
          <div className="text-[10px] text-stone-400 pr-2 text-right pt-1">{String(h).padStart(2, "0")}:00</div>
          {data.days.map((d) => (
            <div key={d} data-testid={`week-cell-${d}-${h}`} className="min-h-[44px] border-l border-stone-100 p-0.5 space-y-0.5 hover:bg-indigo-50/40"
                 onDragOver={(e) => e.preventDefault()} onDrop={(e) => onDrop(e, d, h)}>
              {at(d, h).map((it) => <Card key={it.item_id} it={it} onOpen={onOpen} />)}
            </div>
          ))}
        </div>
      ))}
    </div>
  );
}

function MobileList({ data, onOpen }) {
  return (
    <div className="md:hidden space-y-3" data-testid="week-mobile-list">
      {data.days.map((d) => {
        const items = data.items.filter((i) => i.local_date === d);
        return (
          <div key={d}>
            <div className="text-xs font-bold text-stone-600 mb-1">{new Date(`${d}T12:00:00`).toLocaleDateString(undefined, { weekday: "long", day: "numeric", month: "short" })}</div>
            {items.length ? <div className="space-y-1">{items.map((it) => <Card key={it.item_id} it={it} onOpen={onOpen} compact />)}</div>
              : <div className="text-[11px] text-stone-400">Nothing scheduled</div>}
          </div>
        );
      })}
      <p className="text-[11px] text-stone-500">Tap a post to move or approve it.</p>
    </div>
  );
}

export function CalendarWeekView({ bizId, tick }) {
  const [start, setStart] = useState(null);
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [open, setOpen] = useState(null);
  const load = useCallback(async () => {
    setError(null);
    try {
      const q = new URLSearchParams({ business_id: bizId, ...(start ? { start } : {}) });
      setData((await api.get(`/aina/automation/calendar/week?${q}`)).data);
    } catch (e) { setError(errMsg(e)); }
  }, [bizId, start]);
  useEffect(() => { load(); }, [load, tick]);

  const onDrop = async (e, day, hour) => {
    e.preventDefault();
    let p;
    try { p = JSON.parse(e.dataTransfer.getData("text/plain")); } catch { return; }
    const local = `${day}T${String(hour).padStart(2, "0")}:${p.t.slice(3, 5)}`;
    try {
      await api.post(`/aina/automation/calendar/${p.id}/reschedule`, { local_datetime: local, expected_version: p.v });
      toast.success(`Moved to ${day} ${local.slice(11)}`);
    } catch (err) { toast.error(errMsg(err)); }
    load();
  };

  if (error) return (
    <div className="text-sm text-rose-700" data-testid="week-error">{error} <button className="underline" onClick={load} data-testid="week-retry">Retry</button></div>
  );
  if (!data) return <Loader2 className="w-4 h-4 animate-spin" />;
  return (
    <div data-testid="calendar-week-view">
      <div className="flex flex-wrap items-center gap-2 mb-3">
        <button data-testid="week-prev" onClick={() => setStart(data.prev_week)} className="p-1.5 rounded-full bg-stone-100"><ChevronLeft className="w-4 h-4" /></button>
        <button data-testid="week-today" onClick={() => setStart(null)} className="px-3 py-1 rounded-full bg-stone-100 text-xs font-semibold">This week</button>
        <button data-testid="week-next" onClick={() => setStart(data.next_week)} className="p-1.5 rounded-full bg-stone-100"><ChevronRight className="w-4 h-4" /></button>
        <span className="text-sm font-bold" data-testid="week-range">{data.days[0]} → {data.days[6]}</span>
        <span className="text-[11px] text-stone-500" data-testid="week-timezone">Times in {data.timezone}</span>
      </div>
      {!data.items.length && <p className="text-xs text-stone-500 mb-2" data-testid="week-empty">No posts this week. Generate a plan or add a draft to the calendar.</p>}
      <Grid data={data} onOpen={setOpen} onDrop={onDrop} />
      <MobileList data={data} onOpen={setOpen} />
      {open && <DetailPanel it={open} tz={data.timezone} onClose={() => setOpen(null)} onChanged={load} />}
    </div>
  );
}
